import type { SupportedLocale } from '@erp/shared';

const de = {
  title: 'Einführung und Betriebsmodus',
  banner: 'Einführungsmodus aktiv – Testtransaktionen werden vor dem Echtbetrieb gelöscht.',
  unknown: 'Betriebsmodus unbekannt – vor weiteren Änderungen Status prüfen.',
  loading: 'Betriebsmodus wird geprüft …',
  failed: 'Betriebsmodus konnte nicht geladen werden.',
  retry: 'Erneut prüfen',
  manage: 'Einführung verwalten',
  phase: 'Phase',
  review: 'Bestand und Bereinigungsumfang prüfen',
  preserved: 'Erhaltene Datensätze',
  tests: 'Zugeordnete Testdatensätze',
  files: 'Zugeordnete Testdateien',
  blockers: 'Sperren',
  retained:
    'Benutzer, Rechte, Einstellungen, Geschäftspartner, Artikel, Rezepturen, Preislisten, Vorlagen und Integrationskonfiguration bleiben erhalten.',
  safety:
    'Aktivierung und Bereinigung bleiben gesperrt, solange die vollständige Löschabdeckung, der verifizierte Backupablauf und die Absicherung von Dateien und Hintergrundarbeit nicht abgenommen sind. Die Vorprüfung löscht und kennzeichnet keine vorhandenen Daten um.',
  backups:
    'Sicherheitsbackups bleiben nach dem bestehenden Aufbewahrungskonzept erhalten. Ein Restore muss den gesicherten Betriebsmodus wiederherstellen; wiederhergestellte Testdaten dürfen nicht als Echtbetrieb freigegeben werden.',
  noAccess: 'Für diese Ansicht fehlt die Berechtigung.',
  empty: 'Keine zugeordneten Testdaten vorhanden.',
  UNCLASSIFIED: 'Bestand geschützt · Einführung nicht aktiviert',
  INTRODUCTION: 'Einführungsmodus',
  CLEANING: 'Bereinigung läuft',
  CLEANUP_FAILED: 'Bereinigung fehlgeschlagen · Schreiben gesperrt',
  LIVE: 'Echtbetrieb',
  production: 'Produktion',
  staging: 'Staging',
  test: 'Testumgebung',
  development: 'Entwicklung',
  environmentUnknown: 'Umgebung unbekannt',
  EXISTING_TRANSACTIONS: 'Vorhandene Bewegungsdaten ohne freigegebene Testzuordnung',
  SETUP_RELEASED: 'Vorhandene Einrichtungsfreigabe',
  LIVE_IRREVERSIBLE: 'Echtbetrieb kann nicht in den Einführungsmodus zurückgesetzt werden',
  UNCLASSIFIED_DEPENDENCIES: 'Abhängigkeiten mit unklarer Herkunft',
  BACKUP_BRIDGE_UNAVAILABLE: 'Geprüfter Backupablauf nicht angebunden',
  COVERAGE_INCOMPLETE: 'Vollständiger Bereinigungsablauf noch nicht abgenommen',
};
type Copy = Record<keyof typeof de, string>;
export const introductionTranslations: Record<SupportedLocale, Copy> = {
  de,
  en: {
    title: 'Introduction and operating mode',
    banner: 'Introduction mode active – test transactions will be deleted before live operation.',
    unknown: 'Operating mode unknown – check the status before making changes.',
    loading: 'Checking operating mode …',
    failed: 'Operating mode could not be loaded.',
    retry: 'Check again',
    manage: 'Manage introduction',
    phase: 'Phase',
    review: 'Review existing data and cleanup scope',
    preserved: 'Preserved records',
    tests: 'Assigned test records',
    files: 'Assigned test files',
    blockers: 'Blockers',
    retained:
      'Users, permissions, settings, business partners, products, recipes, price lists, templates and integration configuration are preserved.',
    safety:
      'Activation and cleanup remain blocked until complete deletion coverage, verified backups and protection of files and background work have passed acceptance. This review neither deletes nor relabels existing data.',
    backups:
      'Safety backups remain subject to the existing retention policy. A restore must restore the saved operating mode; restored test data must never be released as live transactions.',
    noAccess: 'You do not have permission to access this view.',
    empty: 'No assigned test data.',
    UNCLASSIFIED: 'Existing data protected · introduction inactive',
    INTRODUCTION: 'Introduction mode',
    CLEANING: 'Cleanup in progress',
    CLEANUP_FAILED: 'Cleanup failed · writes blocked',
    LIVE: 'Live operation',
    production: 'Production',
    staging: 'Staging',
    test: 'Test environment',
    development: 'Development',
    environmentUnknown: 'Unknown environment',
    EXISTING_TRANSACTIONS: 'Existing transactions without approved test provenance',
    SETUP_RELEASED: 'Existing setup approval',
    LIVE_IRREVERSIBLE: 'Live operation cannot return to introduction mode',
    UNCLASSIFIED_DEPENDENCIES: 'Dependencies of unknown origin',
    BACKUP_BRIDGE_UNAVAILABLE: 'Verified backup process is not connected',
    COVERAGE_INCOMPLETE: 'Complete cleanup process has not passed acceptance',
  },
  tr: {
    title: 'Devreye alma ve işletim modu',
    banner: 'Devreye alma modu etkin – test işlemleri canlı kullanımdan önce silinecek.',
    unknown: 'İşletim modu bilinmiyor – değişiklik yapmadan önce durumu kontrol edin.',
    loading: 'İşletim modu kontrol ediliyor …',
    failed: 'İşletim modu yüklenemedi.',
    retry: 'Tekrar kontrol et',
    manage: 'Devreye almayı yönet',
    phase: 'Aşama',
    review: 'Mevcut verileri ve temizleme kapsamını incele',
    preserved: 'Korunan kayıtlar',
    tests: 'Atanan test kayıtları',
    files: 'Atanan test dosyaları',
    blockers: 'Engeller',
    retained:
      'Kullanıcılar, yetkiler, ayarlar, iş ortakları, ürünler, reçeteler, fiyat listeleri, şablonlar ve entegrasyon yapılandırmaları korunur.',
    safety:
      'Eksiksiz silme kapsamı, doğrulanmış yedekleme ve dosyalar ile arka plan işlerinin korunması kabul edilene kadar etkinleştirme ve temizleme engellenir. Bu ön inceleme mevcut verileri silmez veya yeniden etiketlemez.',
    backups:
      'Güvenlik yedekleri mevcut saklama politikasına tabidir. Geri yükleme, kaydedilmiş işletim modunu geri getirmelidir; geri yüklenen test verileri canlı işlemler olarak kullanıma açılamaz.',
    noAccess: 'Bu görünüm için yetkiniz yok.',
    empty: 'Atanmış test verisi yok.',
    UNCLASSIFIED: 'Mevcut veriler korunuyor · devreye alma etkin değil',
    INTRODUCTION: 'Devreye alma modu',
    CLEANING: 'Temizleme sürüyor',
    CLEANUP_FAILED: 'Temizleme başarısız · yazma engellendi',
    LIVE: 'Canlı işletim',
    production: 'Üretim ortamı',
    staging: 'Staging',
    test: 'Test ortamı',
    development: 'Geliştirme',
    environmentUnknown: 'Ortam bilinmiyor',
    EXISTING_TRANSACTIONS: 'Onaylı test kaynağı olmayan mevcut işlemler',
    SETUP_RELEASED: 'Mevcut kurulum onayı',
    LIVE_IRREVERSIBLE: 'Canlı işletim devreye alma moduna döndürülemez',
    UNCLASSIFIED_DEPENDENCIES: 'Kaynağı bilinmeyen bağımlılıklar',
    BACKUP_BRIDGE_UNAVAILABLE: 'Doğrulanmış yedekleme süreci bağlı değil',
    COVERAGE_INCOMPLETE: 'Eksiksiz temizleme süreci henüz kabul edilmedi',
  },
  fr: {
    title: 'Mise en service et mode de fonctionnement',
    banner:
      'Mode de mise en service actif – les transactions de test seront supprimées avant l’exploitation réelle.',
    unknown: 'Mode inconnu – vérifiez le statut avant toute modification.',
    loading: 'Vérification du mode …',
    failed: 'Le mode de fonctionnement n’a pas pu être chargé.',
    retry: 'Vérifier à nouveau',
    manage: 'Gérer la mise en service',
    phase: 'Phase',
    review: 'Examiner les données et le périmètre du nettoyage',
    preserved: 'Enregistrements conservés',
    tests: 'Enregistrements de test attribués',
    files: 'Fichiers de test attribués',
    blockers: 'Blocages',
    retained:
      'Les utilisateurs, droits, paramètres, partenaires, articles, recettes, tarifs, modèles et configurations d’intégration sont conservés.',
    safety:
      'L’activation et le nettoyage restent bloqués jusqu’à la validation du périmètre complet de suppression, des sauvegardes vérifiées et de la protection des fichiers et tâches en arrière-plan. Cet examen ne supprime ni ne requalifie les données existantes.',
    backups:
      'Les sauvegardes de sécurité restent soumises à la politique de conservation existante. Une restauration doit rétablir le mode sauvegardé ; les données de test restaurées ne doivent jamais être autorisées comme transactions réelles.',
    noAccess: 'Vous n’avez pas les droits pour cette vue.',
    empty: 'Aucune donnée de test attribuée.',
    UNCLASSIFIED: 'Données existantes protégées · mise en service inactive',
    INTRODUCTION: 'Mode de mise en service',
    CLEANING: 'Nettoyage en cours',
    CLEANUP_FAILED: 'Échec du nettoyage · écritures bloquées',
    LIVE: 'Exploitation réelle',
    production: 'Production',
    staging: 'Staging',
    test: 'Environnement de test',
    development: 'Développement',
    environmentUnknown: 'Environnement inconnu',
    EXISTING_TRANSACTIONS: 'Transactions existantes sans provenance de test approuvée',
    SETUP_RELEASED: 'Validation de configuration existante',
    LIVE_IRREVERSIBLE: 'L’exploitation réelle ne peut pas revenir au mode de mise en service',
    UNCLASSIFIED_DEPENDENCIES: 'Dépendances d’origine inconnue',
    BACKUP_BRIDGE_UNAVAILABLE: 'Le processus de sauvegarde vérifié n’est pas raccordé',
    COVERAGE_INCOMPLETE: 'Le nettoyage complet n’a pas encore été validé',
  },
  es: {
    title: 'Puesta en marcha y modo operativo',
    banner:
      'Modo de puesta en marcha activo – las transacciones de prueba se eliminarán antes de la operación real.',
    unknown: 'Modo operativo desconocido – compruebe el estado antes de realizar cambios.',
    loading: 'Comprobando el modo operativo …',
    failed: 'No se pudo cargar el modo operativo.',
    retry: 'Volver a comprobar',
    manage: 'Gestionar la puesta en marcha',
    phase: 'Fase',
    review: 'Revisar los datos y el alcance de la limpieza',
    preserved: 'Registros conservados',
    tests: 'Registros de prueba asignados',
    files: 'Archivos de prueba asignados',
    blockers: 'Bloqueos',
    retained:
      'Se conservan usuarios, permisos, ajustes, socios comerciales, artículos, recetas, listas de precios, plantillas y configuraciones de integración.',
    safety:
      'La activación y la limpieza permanecen bloqueadas hasta validar la cobertura completa de eliminación, las copias verificadas y la protección de archivos y tareas en segundo plano. Esta revisión no elimina ni reclasifica datos existentes.',
    backups:
      'Las copias de seguridad siguen sujetas a la política de conservación existente. Una restauración debe recuperar el modo operativo guardado; los datos de prueba restaurados nunca deben autorizarse como transacciones reales.',
    noAccess: 'No tiene permiso para acceder a esta vista.',
    empty: 'No hay datos de prueba asignados.',
    UNCLASSIFIED: 'Datos existentes protegidos · puesta en marcha inactiva',
    INTRODUCTION: 'Modo de puesta en marcha',
    CLEANING: 'Limpieza en curso',
    CLEANUP_FAILED: 'Limpieza fallida · escrituras bloqueadas',
    LIVE: 'Operación real',
    production: 'Producción',
    staging: 'Staging',
    test: 'Entorno de prueba',
    development: 'Desarrollo',
    environmentUnknown: 'Entorno desconocido',
    EXISTING_TRANSACTIONS: 'Transacciones existentes sin procedencia de prueba aprobada',
    SETUP_RELEASED: 'Aprobación de configuración existente',
    LIVE_IRREVERSIBLE: 'La operación real no puede volver al modo de puesta en marcha',
    UNCLASSIFIED_DEPENDENCIES: 'Dependencias de origen desconocido',
    BACKUP_BRIDGE_UNAVAILABLE: 'El proceso de copia verificada no está conectado',
    COVERAGE_INCOMPLETE: 'La limpieza completa aún no ha sido validada',
  },
};
